import { DatePipe, ViewportScroller } from '@angular/common';
import {Component, Inject, Input, OnInit} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {Locale, WINDOW} from '@biletix/core/common';
import { RouteParameter } from '@biletix/core/common/constants/route-parameter.const';
import { ImageUrlManager } from '@biletix/core/common/helper/image-url-manager';
import { ImageProcessing } from '@biletix/core/common/utils/image-processing';
import { ArtistDetail } from '@biletix/core/models/event/artist-detail.model';
import { EventsDetailByArtist } from '@biletix/core/models/event/events-detail-by-artist.model';
import { EventService } from '@biletix/core/services/event.service';
import { I18nService } from '@biletix/core/services/i18n.service';
import { TabItem } from '@biletix/core/view-models/tab-item.view.model';
import { TranslateService } from '@ngx-translate/core'; 
import { AbstractionComponent } from '@biletix/core/common/abstraction-component';
import { ApplicationStateService } from '@biletix/core/services/application-state.service';
import {environment} from "@biletix/env";
import {ShareButtonsConfig} from "@biletix/shared/components/share-button/share-button.component";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {FancyboxLoaderService} from "@biletix/core/services/fancybox-loader.service";
import {of} from "rxjs";
import { SeoService } from '@biletix/core/services/seo.service';
import { CanonicalService } from '@biletix/core/services/canonical.service';
import { SeoPagePath, SeoUrlService } from '@biletix/core/services/seo-url.service';
import { StructuredDataService } from '@biletix/core/services/structured-data.service';
import {
  SCHEMA_DATE_FORMAT,
  SCHEMA_TIMEZONE,
  buildEventItemList,
  pruneEmpty,
  toPlainText,
} from '@biletix/core/common/utils/structured-data.util';
import { resolveListItemPrice } from '@biletix/core/common/utils/ga4-ecommerce.util';

@Component({
  standalone: false,
  selector: 'btx-artist-event-list',
  templateUrl: './artist-event-list.component.html',
  styleUrls: ['./artist-event-list.component.scss'],
})
export class ArtistEventListComponent extends AbstractionComponent  implements OnInit {
  locales: Locale[];
  currentLocale: Locale;

  calendarOrListStatus: boolean = false;
  date: string | number | Date = new Date();
  @Input() region?: string;
  @Input() lang?: string;
  artistName: string = '';
  artistLogoImage: string = '';
  artistDescription: string = '';
  artistTextDescription:string = '';
  artistWebUrl: string = '';
  artistFacebookUrl: string = '';
  artistYoutubeUrl: string = '';
  artistInstagramUrl: string = '';
  artistXUrl: string = '';
  artistSpotifyUrl: string = '';
  artistId: string='';
  spotifyId: string='';
  artistProfilePhoto : string='';
  videoURL:string[]=[];

  artistImageSrc!: string;
  artistPhotoItems: string[] = [];
  mobileGallery: string[] = [];
  config!: ShareButtonsConfig;

  artistImage: string | any;
  wideImage: string ='';

  color: string = '#00000';
  artistDetail!: ArtistDetail;
  eventsDetailByArtist: EventsDetailByArtist[] = [];
  maxLengthValue!: number;

  links!: TabItem[];
  activeLink!: TabItem;

  eventNoSrc!: string;

  artistSmallProfilePhoto : string='';
  artistMidProfilePhoto : string='';

  /** JSON-LD script'inin id'si; aynı id ile yazmak bloğu çoğaltmaz (bkz. StructuredDataService). */
  private static readonly SCHEMA_ELEMENT_ID = 'btx-artist-schema';

  /**
   * `initSeo()` içinde hesaplanan kanonik URL; JSON-LD'de `@id`/`url` olarak
   * yeniden kullanılır. Etkinlik listesi daha sonra geldiğinde blok yeniden
   * yazılırken URL'i ikinci kez üretmemek için saklanır.
   */
  private schemaCanonicalUrl: string = '';

  constructor(
    @Inject(WINDOW) private window: Window,
    private eventService: EventService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private datePipe: DatePipe,
    private i18nService: I18nService,
    private sanitizer: DomSanitizer,
    private seo: SeoService,
    private canonicalService: CanonicalService,
    private seoUrlService: SeoUrlService,
    private structuredDataService: StructuredDataService,
    private dialog: MatDialog,
    private ApplicationStateService: ApplicationStateService,
    private  viewScroller: ViewportScroller,
    private fancyboxLoader: FancyboxLoaderService
  ) {
    super();
    this.locales = this.i18nService.getAvailableLocales();
    this.currentLocale = this.i18nService.currentLocale;
    this.eventsDetailByArtist = this.groupEventForList();
    this.calendarOrListStatus = this.eventsDetailByArtist?.length >= 5;

  }

  ngOnInit(): void {
    this.initializeVariables();
    this.fancyboxLoader.loadWhenIdle();

    let artistCode = this.activatedRoute.snapshot.params[RouteParameter.PARAMS.ARTIST_CODE];

    // ArtistDetailResolver aynı veriyi rota çözülürken zaten çekiyor. Onu tekrar
    // istemek SSR sırasında backend'e ikinci bir çağrı demek olurdu; resolver
    // verisi varsa kullanılır. Geçici servis hatasında resolver `null` çözer
    // (503 set edilir) ve o durumda istek yeniden denenir.
    const resolvedArtistDetail: ArtistDetail | null =
      this.activatedRoute.snapshot.data['artistDetail'] ?? null;

    const artistDetail$ = resolvedArtistDetail
      ? of(resolvedArtistDetail)
      : this.eventService.getArtistDetail(artistCode);

    artistDetail$.pipe(this.takeUntilDestroy())
      .subscribe((artistDetail) => {
        this.artistDetail = artistDetail;
        this.artistId = artistDetail.artistId;
        this.artistName = artistDetail.artistName;
        this.artistDescription = artistDetail.artistDescription;
        this.artistTextDescription = artistDetail.artistTextDescription;
        this.artistLogoImage = artistDetail.artistProfilePhoto;
        this.wideImage = artistDetail.artistWideImage;
        this.artistWebUrl = artistDetail.webURL;
        this.artistFacebookUrl = artistDetail.fbURL;
        this.artistInstagramUrl = artistDetail.instaURL;
        this.artistXUrl = artistDetail.twitterURL;
        this.artistSpotifyUrl = artistDetail.spotifyURL;
        this.artistYoutubeUrl = artistDetail.youtubeURL;
        this.videoURL = artistDetail.videoURL?.split(',').map(v=>v.trim());
        this.spotifyId = artistDetail.spotifyId;
        this.artistProfilePhoto = artistDetail.artistProfilePhoto;


        this.artistImageSrc = this.getProfilePhotoImage();
        this.artistLogoImage = this.getProfilePhotoImage();
        this.artistPhotoItems = this.getArtistImages();
        this.mobileGallery.push(this.artistImageSrc, ...this.artistPhotoItems);
        this.artistProfilePhoto = this.getProfilePhotoImage();
        this.wideImage = this.getArtistWideImage();
        this.maxLengthValue = this.getIsMobile() ? 300 : 10000;
        this.eventNoSrc = this.getNoImage();

        this.artistSmallProfilePhoto = this.getSmallProfilePhotoImage();
        this.artistMidProfilePhoto = this.getMidProfilePhotoImage();

        this.initSeo(artistDetail);
      });

    this.eventService
      .getAllEventsDetailByArtist(artistCode).pipe(this.takeUntilDestroy())
      .subscribe((eventsDetailByArtist) => {
        this.eventsDetailByArtist = eventsDetailByArtist;
        this.enrichSeoDescription(eventsDetailByArtist);
        // Etkinlik listesi sanatçı detayından ayrı ve daha sonra geliyor; blok
        // liste geldiğinde etkinliklerle birlikte yeniden yazılır.
        this.addSchema();
      });

    this.links = this.getIsMobile()
      ? [
        {label: 'performance.labels.eventCalendar', anchor: 'eventCalendar'},
        {label: 'account.labels.packageAbout', anchor: 'about'},
        {label: 'shared.labels.followTheArtist', anchor: 'followArtist'},
        {label: 'shared.labels.mostListened', anchor: 'spotify'},
        {label: 'shared.labels.pastEvents', anchor: 'pastPerformance'}
      ]
      : [
        {label: 'performance.labels.eventCalendar', anchor: 'eventCalendar'},
        {label: 'account.labels.packageAbout', anchor: 'about'}
      ];

    this.activeLink = this.links[0];

  }

  initializeVariables() {
    this.config = {
      url: this.window.location.href,
      description: this.artistTextDescription,
      title: this.artistName,
      image: this.getArtistImage()
    };
  }

  /**
   * Sanatçı sayfasının sosyal medya paylaşım tag'larını set eder.
   *
   * Bu sayfa SSR ile servis ediliyor (bkz. src/server.ts içindeki `ssrRoutes`
   * allowlist'inde `/artist` var) ve WhatsApp/Slack/Facebook crawler'ları
   * JavaScript çalıştırmadığı için tag'ların sunucudan gelen HTML'de hazır
   * olması gerekir. Resolver verisi rota çözülürken hazır olduğundan bu blok
   * SSR render'ı sırasında da çalışır.
   *
   * Açıklamada `artistTextDescription` tercih edilir: `artistDescription` HTML
   * içerebiliyor, og:description ise düz metin olmak zorunda.
   *
   * Kanonik URL slug'SIZ formdur (`artist/{artistCode}/TURKIYE/{dil}`). Rota
   * slug'lı varyantı da kabul ediyor ama slug servis verisinde yok —
   * `ArtistDetail`'da `keyUrlText` alanı bulunmuyor, iç link'ler slug'ı etkinlik
   * detayından alıyor. Kanonik sinyalin istenen URL'e göre değişmemesi gerektiği
   * için yalnızca veriden türetilebilen slug'sız form kanonik seçilir.
   *
   * Başlık/açıklama/anahtar kelimeler i18n üzerinden kurulur: sayfa hem `/tr`
   * hem `/en` altında servis ediliyor, metinlerin koda gömülmesi İngilizce
   * sürümde Türkçe başlık üretiyordu.
   */
  private initSeo(artistDetail: ArtistDetail): void {
    const artistName = artistDetail.artistName || '';
    const pagePath: SeoPagePath = {
      segments: ['artist', this.activatedRoute.snapshot.params[RouteParameter.PARAMS.ARTIST_CODE]],
    };
    const langParam = this.activatedRoute.snapshot.params[RouteParameter.PARAMS.LANGUAGE];
    const canonicalUrl = this.seoUrlService.buildCanonicalUrl(pagePath, langParam);

    this.translateService
      .stream(
        [
          'shared.seo.artistTitle',
          'shared.seo.artistDescription',
          'shared.seo.artistKeywords',
          'shared.labels.siteKeywords',
        ],
        { artistName },
      )
      .pipe(this.takeUntilDestroy())
      .subscribe((translations) => {
        this.seo.setTags({
          title: translations['shared.seo.artistTitle'],
          titleSuffix: '| Biletix',
          description:
            artistDetail.artistTextDescription ||
            artistDetail.artistDescription ||
            translations['shared.seo.artistDescription'],
          image: ImageUrlManager.toSocialShareImage(this.artistImageSrc),
          keywords:
            (translations['shared.seo.artistKeywords'] ?? '') +
            (translations['shared.labels.siteKeywords'] ?? ''),
          // og:url canonical ile aynı olmalı; document.URL SSR'da http üretiyordu.
          url: canonicalUrl,
        });
      });

    this.canonicalService.setCanonicalUrl(canonicalUrl);
    this.canonicalService.setAlternateUrls(this.seoUrlService.buildAlternateUrls(pagePath));
    this.schemaCanonicalUrl = canonicalUrl;
    this.addSchema();
  }

  /**
   * Sanatçı sayfasının JSON-LD'sini yayınlar: `PerformingGroup` + etkinlik
   * `ItemList`'i.
   *
   * İki kaynak farklı zamanlarda geliyor (detay resolver'dan, etkinlikler ayrı
   * çağrıdan), bu yüzden metot iki yerden de çağrılır; `setJsonLd` id bazlı
   * idempotent olduğu için ikinci çağrı bloğu çoğaltmaz, üzerine yazar.
   */
  private addSchema(): void {
    if (!this.artistDetail || !this.schemaCanonicalUrl) {
      return;
    }

    const blocks = [
      this.createArtistSchema(this.artistDetail, this.schemaCanonicalUrl),
      this.createArtistEventListSchema(),
    ].filter((block) => !!block);

    this.structuredDataService.setJsonLd(
      ArtistEventListComponent.SCHEMA_ELEMENT_ID,
      blocks.length ? blocks : null,
    );
  }

  /**
   * Sanatçıyı `PerformingGroup` olarak tanımlar.
   *
   * `Person` yerine `PerformingGroup`: veride sanatçının kişi mi topluluk mu
   * olduğunu ayırt eden bir alan yok (`ArtistDetail`), `PerformingGroup` ise iki
   * durumda da geçerli ve etkinlik sayfalarındaki `performer` tipiyle tutarlı.
   *
   * `sameAs` sosyal medya profilleri: Google varlık eşleştirmesinde (knowledge
   * panel) bunları kullanır; boş alanlar `pruneEmpty` ile hiç yazılmaz.
   */
  private createArtistSchema(
    artistDetail: ArtistDetail,
    canonicalUrl: string,
  ): Record<string, unknown> | null {
    if (!artistDetail?.artistName) {
      return null;
    }

    return pruneEmpty<Record<string, unknown>>({
      '@context': 'https://schema.org',
      '@type': 'PerformingGroup',
      '@id': canonicalUrl,
      url: canonicalUrl,
      name: artistDetail.artistName,
      description:
        toPlainText(artistDetail.artistTextDescription) ??
        toPlainText(artistDetail.artistDescription),
      image: ImageUrlManager.toSocialShareImage(this.artistImageSrc),
      sameAs: [
        artistDetail.webURL,
        artistDetail.fbURL,
        artistDetail.instaURL,
        artistDetail.twitterURL,
        artistDetail.youtubeURL,
        artistDetail.spotifyURL,
      ],
    });
  }

  /** Sanatçının satışta olan etkinlikleri; kayıtlar kendi kanonik URL'lerine bağlanır. */
  private createArtistEventListSchema(): Record<string, unknown> | null {
    return buildEventItemList(
      this.artistDetail?.artistName ?? '',
      (this.eventsDetailByArtist ?? []).map((item) => ({
        url: this.seoUrlService.buildCanonicalUrl(
          { segments: ['etkinlik', item.eventCode], slug: item.keyUrlText },
          this.activatedRoute.snapshot.params[RouteParameter.PARAMS.LANGUAGE],
        ),
        name: item.eventName,
        startDate: this.datePipe.transform(
          item.performanceDate,
          SCHEMA_DATE_FORMAT,
          SCHEMA_TIMEZONE,
        ),
        venueName: item.venueName,
        venueCity: item.venueCity,
        price: resolveListItemPrice(item.minPrice, item.priceInfo),
      })),
    );
  }

  /**
   * Sanatçının kendi tanıtım metni yoksa, açıklamayı yaklaşan ilk etkinliğin
   * şehir ve tarih bilgisiyle zenginleştirir.
   *
   * Etkinlik listesi `artistDetail`'den ayrı ve daha sonra geldiği için SEO
   * tag'ları önce `initSeo()` ile güvenli değerlerle kurulur; bu metot yalnızca
   * veri gerçekten geldiyse açıklamanın üzerine yazar. Böylece etkinlik çağrısı
   * hata alırsa ya da boş dönerse sayfa açıklamasız kalmaz.
   */
  private enrichSeoDescription(events: EventsDetailByArtist[]): void {
    const hasOwnDescription = !!(
      this.artistDetail?.artistTextDescription || this.artistDetail?.artistDescription
    );
    if (hasOwnDescription) {
      return;
    }

    const nextEvent = events?.find((event) => !!event.venueCity && !!event.dateInfo);
    if (!nextEvent) {
      return;
    }

    this.translateService
      .stream('shared.seo.artistDescriptionWithEvent', {
        artistName: this.artistDetail?.artistName || '',
        venueCity: nextEvent.venueCity,
        dateInfo: nextEvent.dateInfo,
      })
      .pipe(this.takeUntilDestroy())
      .subscribe((description) => this.seo.setTags({ description }));
  }

  getArtistImage(): string {
    return ImageUrlManager.createArtistImage(this.artistLogoImage);
  }
  private getArtistImages(): string[] {
    const images = this.artistDetail.images ?? [];
    return images.map((url: string) => ImageUrlManager.create(url, environment.artistImageBasePath));
  }

  isAvifImage(src?: string | null): boolean {
    if (!src) {
      return false;
    }
    return src.toLowerCase().endsWith('.avif');
  }

  getArtistWideImage(): string {
    return ImageUrlManager.createArtistWideImage(this.wideImage);
  }

  getProfilePhotoImage(): string {
    return ImageUrlManager.createArtistProfilePhotoImage(this.artistProfilePhoto);
  }

  getNoImage(): string {
    return ImageUrlManager.createEventImage("noimage.gif");
  }

  getSmallProfilePhotoImage(): string {
    return ImageUrlManager.createSmallArtistProfilePhotoImage(this.artistDetail.artistProfilePhoto);
  }

  getMidProfilePhotoImage(): string {
    return ImageUrlManager.createMidArtistProfilePhotoImage(this.artistDetail.artistProfilePhoto);
  }



  groupEventForList():EventsDetailByArtist[] {
    const eventMap:Map<string, EventsDetailByArtist> = new Map<string, EventsDetailByArtist>();

    const priorityList = ['s01_onsale', 's03_soon', 's05_postponed', 's02_soldout', 's12_checkbacklater',
      's04_canceled','s00_closed','s06_boxoffice','s16_rescheduled','s17_venue_changed','s14_other_channels','s13_old_over',];

    this.eventsDetailByArtist.forEach(event => {
      if (!eventMap.has(event.eventCode)) {
        eventMap.set(event.eventCode,event);
        eventMap.get(event.eventCode)!.firstPerformanceDate = event.performanceDate;
        eventMap.get(event.eventCode)!.lastPerformanceDate = event.performanceDate;
      } else {
        const firstEvent = eventMap.get(event.eventCode);
        if (firstEvent && firstEvent.firstPerformanceDate && firstEvent.firstPerformanceDate > event.performanceDate) {
          eventMap.get(event.eventCode)!.firstPerformanceDate = event.performanceDate;
        }
        if (firstEvent && firstEvent.lastPerformanceDate && firstEvent.lastPerformanceDate < event.performanceDate) {
          eventMap.get(event.eventCode)!.lastPerformanceDate = event.performanceDate;
        }
        if (priorityList.indexOf(firstEvent!.status) > priorityList.indexOf(event.status)) {
          eventMap.get(event.eventCode)!.status = event.status;
        }
      }
    })
    return [...eventMap.values()];
  }

  getFormattedUrl(url: string): string {
    return url.replace(/(^\w+:|^)\/\/www\./, '').replace(/(^\w+:|^)\/\//, '').replace(/\/$/, '');;
  }

  initFragment() {
    let fragment: string | any = this.activatedRoute.snapshot.fragment;
    const currentLink = this.links
        .filter(item => item.anchor != null)
        .find(item => item.anchor == fragment);

    if (currentLink) {
        this.activeLink = currentLink;
        this.scrollToAnchor(fragment);
    }
}

onClickTabItem(link: TabItem) {
  this.activeLink = link;

  if (link.anchor) {
      this.scrollToAnchor(link.anchor);
  }

  if (link.dialog) {
      this.openModal(link.component);
  }

if (link.href) {
  window.open(link.href, '_blank');
}
}

openModal(component: any) {
  this.dialog.open(component, {
          width: '100%',
          maxWidth: '1220px'
      }
  );
}

  isPhotoGalleryVisible(): boolean {
    return this.artistPhotoItems.length > 0;
  }

  getIsMobile() {
    return this.ApplicationStateService.getIsMobileResolution();
  }

scrollToAnchor(anchor: string) {
  this.viewScroller.scrollToAnchor(anchor);
}

  getVideoUrl(video:string): SafeResourceUrl {
    return `https://www.youtube.com/embed/${video}`;
  }

onLoadImage(imageRef: HTMLImageElement) {
  if (imageRef) {
      const a = ImageProcessing.getAverageRGB(imageRef);
      this.color = a.hex();
  }
}

getVideoThumbnail(video: any): string {
  const videoId = video
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

getEmbeddedSpotifyUrl(){
  return 'https://open.spotify.com/embed/artist/' + this.spotifyId + '?utm_source=generator&theme=0';
}

}
